import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Camera, X, ScanLine } from 'lucide-react';

export function BarcodeScanner({ open, onOpenChange, onScanResult }) {
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(function() {
    if (!open) return;

    let html5Qrcode = null;

    async function startScanner() {
      setError('');
      setScanning(true);
      try {
        html5Qrcode = new Html5Qrcode('barcode-reader');
        scannerRef.current = html5Qrcode;

        await html5Qrcode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 280, height: 160 },
            aspectRatio: 1.5
          },
          function(decodedText) {
            // Stop scanner on successful scan
            if (html5Qrcode && html5Qrcode.isScanning) {
              html5Qrcode.stop().then(function() {
                setScanning(false);
                onScanResult(decodedText);
                onOpenChange(false);
              }).catch(function() {
                onScanResult(decodedText);
                onOpenChange(false);
              });
            }
          },
          function() {
            // Ignore scan errors (no barcode found in frame)
          }
        );
      } catch (err) {
        setScanning(false);
        if (err.toString().includes('NotAllowedError')) {
          setError('Camera permission denied. Please allow camera access and try again.');
        } else if (err.toString().includes('NotFoundError')) {
          setError('No camera found on this device.');
        } else {
          setError('Failed to start camera: ' + err.toString());
        }
      }
    }

    // Small delay to ensure the DOM element exists
    const timer = setTimeout(startScanner, 300);

    return function() {
      clearTimeout(timer);
      if (scannerRef.current) {
        try {
          if (scannerRef.current.isScanning) {
            scannerRef.current.stop().catch(function() {});
          }
        } catch (e) { /* ignore */ }
        scannerRef.current = null;
      }
    };
  }, [open, onScanResult, onOpenChange]);

  function handleClose() {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          scannerRef.current.stop().catch(function() {});
        }
      } catch (e) { /* ignore */ }
    }
    setScanning(false);
    setError('');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm p-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera className="w-5 h-5 text-mint-600" />
            Scan Barcode / QR Code
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div
            ref={containerRef}
            className="relative bg-black rounded-lg overflow-hidden"
            style={{ minHeight: '240px' }}
          >
            <div id="barcode-reader" style={{ width: '100%' }} />
            {scanning && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-72 h-40 border-2 border-mint-400 rounded-lg animate-pulse" />
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="p-2 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500 text-center">
              Point your camera at a barcode, QR code, or medicine package. Supports EAN-13, Code128, QR, UPC formats.
            </p>
          </div>

          <Button variant="outline" onClick={handleClose} className="w-full" data-testid="close-scanner-btn">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ScanButton({ onClick, className, size }) {
  return (
    <Button
      type="button"
      variant="outline"
      size={size || 'default'}
      className={className || 'border-mint-300 text-mint-700 hover:bg-mint-50'}
      onClick={onClick}
      data-testid="scan-barcode-btn"
    >
      <ScanLine className="w-4 h-4 mr-1" />
      Scan
    </Button>
  );
}
